import political from "./political.js";
import sectors from "./sectors.js";
import windfall from "./windfall.js";
import wsta from "./wsta.js";
import tourismtax from "./tourismtax.js";
import motability from "./motability.js";
import octoenergy from "./octoenergy.js";
import waspi from "./waspi.js";
import mccain from "./mccain.js";

// Companies that belong to the "Holiday Tax" client group.
// Compared against slugified user.company (lowercase, hyphens).
export const HOLIDAY_TAX_CLIENTS = [
  "hilton",
  "haven",
  "butlins",
  "butlin-s",
  "ukhospitality",
  "stack-data-strategy",
];

// Companies that belong to the "Energy" client group.
export const ENERGY_CLIENTS = [
  "octopus",
  "octoenergy",
  "stack-data-strategy",
];
// Companies that belong to the "WASPI" client group.
export const WASPI_CLIENTS = [
  "waspi",
  "stack-data-strategy",
];
// Companies that belong to the "Motability" client group.
export const MOTABILITY_CLIENTS = [
  "hanbury",
  "motability-operations",
  "stack-data-strategy",
];
// Companies that belong to the "McCain" client group.
export const MCCAIN_CLIENTS = [
  "mccain",
  "stack-data-strategy",
];
// Demo variant question packs (client tab only).
// These control which client questions a demo user can access.
export const DEMO_VARIANT_CLIENT_GROUPS = {
  default: ["windfall"],
  "holiday-tax-demo": [
    "holiday_tax_vote",
    "holiday_tax_support",
    "decline_pride",
    "tourist_tax_concerns",
    "tourist_tax_impact_travel",
  ],
};

export function getPolColours() {
  const DISTINCT = [
    "#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f",
    "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ab"
  ];
  return DISTINCT;
}

export function getPolConfig() {
  const groupsById = {
    ...political,
    ...windfall,
    ...sectors,
    ...wsta,
    ...tourismtax,
    ...motability,
    ...octoenergy,
    ...waspi,
    ...mccain
  };

  return { groupsById };
}

const DISTINCT = ["#4e79a7","#f28e2b","#e15759","#76b7b2","#59a14f","#edc948","#b07aa1","#ff9da7","#9c755f","#bab0ab"];


export function buildLabelColorMaps(groupsById) {
  const labels = {};
  const colors = {};
  let colorIdx = 0;
  for (const g of Object.values(groupsById)) {
    for (const k of g.keys) {
      labels[k] = (g.labels && g.labels[k]) || k;
      if (!colors[k]) {
        if (g.type === "likert") {
          // Use the special likert color function
          colors[k] = baseColorForLikertKey(g, k);
        } else {
          // Default color assignment
          colors[k] = DISTINCT[colorIdx % DISTINCT.length];
          colorIdx += 1;
        }
      }
    }
  }
  return { labels, colors };
}

export function aggregateWeighted(rows, keys, { rowKey, weight = "total", predicate = null } = {}) {
  const acc = Object.create(null);
  const zero = Object.fromEntries(keys.map(k => [k, 0]));

  for (const r of rows || []) {
    if (predicate && !predicate(r)) continue;
    const d = r[rowKey];
    if (!d) continue;
    const w = Number(r[weight] ?? 0);
    if (!acc[d]) acc[d] = { w: 0, sums: { ...zero } };
    acc[d].w += w;
    for (const k of keys) acc[d].sums[k] += Number(r[k] ?? 0) * w;
  }

  const out = {};
  for (const [d, { w, sums }] of Object.entries(acc)) {
    const values = {};
    for (const k of keys) values[k] = w > 0 ? (sums[k] / w) * 100 : 0;
    out[d] = { totalWeight: w, values };
  }
  return out;
}


export function buildDemoPredicate({
    gender = null,     // 1=Men, 2=Women
    age = null,        // 1=18–34, 2=35–64, 3=65+
    ethnicity = null,  // 1..5
    party = null,      // 1..4
    education = null,  // 1=No degree, 2=Degree
  } = {}) {
    return (r) => {
      // NOTE: r.<field> should be numbers in your data rows
      if (gender != null && Number(r.gender) !== Number(gender)) return false;
      if (age != null && Number(r.age) !== Number(age)) return false;
      if (ethnicity != null && Number(r.ethnicity) !== Number(ethnicity)) return false;
      if (party != null && Number(r.party_reg) !== Number(party)) return false;
      if (education != null && Number(r.education) !== Number(education)) return false;
      return true;
    };
  }

  // If you want to show labels elsewhere:
  export const GENDER = [
    { value: null, label: "All" },
    { value: 1,    label: "Men" },
    { value: 2,    label: "Women" },
  ];

  export const AGE = [
    { value: null, label: "All" },
    { value: 1,    label: "18–34" },
    { value: 2,    label: "35–64" },
    { value: 3,    label: "65+" },
  ];

  export const ETHNICITY = [
    { value: null, label: "All" },
    { value: 1,    label: "White" },
    { value: 2,    label: "Black" },
    { value: 3,    label: "Hispanic" },
    { value: 4,    label: "Asian" },
    { value: 5,    label: "Other" },
  ];

  export const PARTY = [
    { value: null, label: "All" },
    { value: 1,    label: "Dem" },
    { value: 2,    label: "Rep" },
    { value: 3,    label: "Ind" },
    { value: 4,    label: "None" },
  ];

  export const EDU = [
    { value: null, label: "All" },
    { value: 1,    label: "Degree" },
    { value: 2,    label: "No degree" },
  ];


export function baseColorForLikertKey(group, key) {
  const pol = group.polarity || {};
  // Find which polarity (pos/neg/neu) corresponds to this key
  const tag = Object.entries(pol).find(([polarity, polKey]) => polKey === key)?.[0];


  if (tag === "pos") return "#2E7D32";
  if (tag === "neg") return "#C62828";
  return "#6e6e6e";
}
